import { FC } from 'react';
import FormButtons from '@/components/form/form-buttons';
import FormSection from '@/components/form/form-section';
import InputControl from '@/components/form/input-control';
import useFormHandle from '@/hooks/use-form-handle.hook';
import useQueryHelper from '@/hooks/use-query-helper';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import { QueryKey } from '@/lib/query-client';
import { getOrgPolicyV1, patchPolicyV1 } from '@/lib/services/policy-v1.service';
import { Box, Grid, Stack } from '@mui/material';
import { useQuery } from 'react-query';
import { Form } from '@/components/form';
import InlineToast from '@/components/toasts/inline';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { FieldError } from '@/lib/services';
import { PublicServiceBlockOptions } from '@/lib/models/policy-model-v1/public-services';
import PsAllowWarningToast from './ps-allow-warning-toast';
import { addProtocol } from '@/lib/url-helpers';
import ServiceControlsV1 from '@/components/form/policy-v1/service-controls-v1';
import NewServicesV1 from '@/components/form/policy-v1/new-services-v1';
import { getAllPrivateModels } from '@/lib/services/private-model.service';
import RerouteControlsV1 from '@/components/form/policy-v1/reroute-controls-v1';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const fields = ['publicServices', 'metadataControls', 'contentControls', 'reroute'];
const formModel = new PolicyModelV1();

const ServiceAccessForm: FC = () => {
  const { errorToast, toast } = useToast();
  const { canChangePolicy, canViewReroute } = useFeature();

  const query = useQuery([QueryKey.OrgPolicyView], async () => getOrgPolicyV1());
  const { data: orgPolicy, refetch } = query;

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError, setFieldValue }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };
      // Normalize redirectUrl: prepend https:// if no protocol is present
      // The Yup transform handles validation, but we still need to update the form state
      // and ensure the normalized value is sent to the API
      if (values.publicServices?.redirectUrl) {
        const normalizedUrl = addProtocol(values.publicServices.redirectUrl.trim());
        setFieldValue('publicServices.redirectUrl', normalizedUrl);
        values.publicServices.redirectUrl = normalizedUrl;
      }

      const { patched, fieldErrors, error } = await patchPolicyV1(
        values.id,
        values.propSlice(fields)
      );
      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The policy was updated');
        refetch();
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const privateModelsQuery = useQuery([QueryKey.PrivateModelsList], async () =>
    getAllPrivateModels()
  );
  const { data: privateModels = [] } = privateModelsQuery;

  const { values } = formHandle;
  const { showLoader } = useQueryHelper(query);
  const { showLoader: privateModelsLoading } = useQueryHelper(privateModelsQuery);

  if (showLoader || privateModelsLoading) {
    return;
  }

  const {
    allowsPublicServices,
    publicServices: { blockAction },
  } = values;

  const hasRedirectUrl = blockAction === 'redirect-url';
  const hasPortalRedirect = blockAction === 'redirect-portal';
  const showPortalRedirectWarning = hasPortalRedirect && !values.portal.enabled;

  return (
    <Form formHandle={formHandle} query={query}>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

      {orgPolicy && (
        <NewServicesV1
          formHandle={formHandle}
          canChangePolicy={canChangePolicy}
          policy={orgPolicy}
        />
      )}

      <FormSection title="Default Service Action" scrollId="default-service-action">
        <FormSectionSubtitle>
          When an attempt is made to access a known service, choose how to handle the request. When
          set to "Blocked", both the Service Block Action, and the Service Access Control List
          settings below will determine further policy decisions.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <InputControl
              label="Default Service Action"
              name="publicServices"
              type="policy-public-action-select-v1"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              policy={orgPolicy!}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Service Block Action"
        scrollId="service-block-action"
        disabled={allowsPublicServices}
      >
        <FormSectionSubtitle>
          When access to a service is blocked in the browser, choose whether users are shown a
          no-access page, or redirected to either the Portal or a custom URL.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <InputControl
              label="Block Action"
              name="publicServices.blockAction"
              type="select"
              options={PublicServiceBlockOptions}
              formHandle={formHandle}
              readonly={!canChangePolicy}
            />
          </Grid>
          <Grid item xs={6} justifyContent="center">
            {hasRedirectUrl && (
              <InputControl
                label="Redirect URL"
                name="publicServices.redirectUrl"
                formHandle={formHandle}
                readonly={!canChangePolicy}
              />
            )}
            {showPortalRedirectWarning && (
              <Stack height="100%" justifyContent="center">
                <Box mt={1}>
                  <InlineToast
                    level="warning"
                    message={`The Portal is not enabled. Please enable it by changing the "Allow Access to Portal" setting above.`}
                  />
                </Box>
              </Stack>
            )}
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="Service Block List" scrollId="blocked-services">
        <FormSectionSubtitle>
          Use the table below to block services for this organization. Services added to this list
          will be blocked regardless of any other policy setting.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputControl
              name="publicServices"
              label="Block List"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="public-service-acl-v1"
              aclVariant="block"
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Service Allow List"
        scrollId="allowed-services"
        disabled={allowsPublicServices}
      >
        <FormSectionSubtitle>
          Use the table below to allow access to services for this organization. Allowed services
          can be monitored or protected. Protected services apply the sensitive data policies to
          those services. Monitored services will be logged with no further action taken.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PsAllowWarningToast policy={values} />
            <InputControl
              name="publicServices"
              label="Allow List"
              formHandle={formHandle}
              readonly={!canChangePolicy}
              type="public-service-acl-v1"
              aclVariant="allow"
            />
          </Grid>
        </Grid>
      </FormSection>

      <ServiceControlsV1 formHandle={formHandle} readonly={!canChangePolicy} policy={orgPolicy!} />

      {canViewReroute && (
        <RerouteControlsV1
          formHandle={formHandle}
          readonly={!canChangePolicy}
          privateModels={privateModels}
        />
      )}
      <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
    </Form>
  );
};

export default ServiceAccessForm;
